import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { clientsApi } from "@/lib/api";

const EMPTY = {
  nom: "",
  prenom: "",
  numero_client: "",
  telephone: "",
  email: "",
  adresse1: "",
  adresse2: "",
  code_postal: "",
  ville: "",
  pays: "France",
  frais_vente: 0,
  observations: "",
  titulaire_compte: "",
  iban: "",
  bic: "",
  rib: "",
};

export default function ClientForm({ mode = "add" }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = mode === "edit";
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    clientsApi
      .get(id)
      .then((c) => {
        if (cancelled) return;
        setForm({ ...EMPTY, ...c });
      })
      .catch(() => toast.error("Client introuvable"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSave = form.nom.trim() && form.adresse1.trim() && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        frais_vente: form.frais_vente ? parseFloat(form.frais_vente) : 0,
      };
      let saved;
      if (isEdit) {
        saved = await clientsApi.update(id, payload);
        toast.success("Client mis à jour");
      } else {
        saved = await clientsApi.create(payload);
        toast.success("Client créé");
      }
      navigate(`/clients/${saved.id}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-[var(--vidoc-text-secondary)]">Chargement...</div>;
  }

  return (
    <div data-testid="client-form-page">
      <PageHeader
        title={isEdit ? "Modifier le client" : "Nouveau client"}
        backTo={isEdit ? `/clients/${id}` : "/clients"}
      />

      <div className="px-4 md:px-8 py-6 max-w-3xl space-y-6 pb-32">
        <Group title="Identité">
          <Row>
            <FormField label="Nom" required value={form.nom} onChange={(v) => set("nom", v)} testid="client-nom" />
            <FormField label="Prénom" value={form.prenom} onChange={(v) => set("prenom", v)} testid="client-prenom" />
          </Row>
          <FormField
            label="N° client externe (référence)"
            value={form.numero_client}
            onChange={(v) => set("numero_client", v)}
            testid="client-numero"
            hint="Optionnel — votre référence comptable"
          />
        </Group>

        <Group title="Contact">
          <Row>
            <FormField label="Téléphone" value={form.telephone} onChange={(v) => set("telephone", v)} testid="client-tel" />
            <FormField label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} testid="client-email" />
          </Row>
        </Group>

        <Group title="Adresse">
          <FormField label="Adresse" required value={form.adresse1} onChange={(v) => set("adresse1", v)} testid="client-adresse1" />
          <FormField label="Complément" value={form.adresse2} onChange={(v) => set("adresse2", v)} testid="client-adresse2" />
          <Row>
            <FormField label="Code postal" value={form.code_postal} onChange={(v) => set("code_postal", v)} testid="client-cp" />
            <FormField label="Ville" value={form.ville} onChange={(v) => set("ville", v)} testid="client-ville" />
            <FormField label="Pays" value={form.pays} onChange={(v) => set("pays", v)} testid="client-pays" />
          </Row>
        </Group>

        <Group title="Comptabilité" collapsible>
          <FormField label="Frais de vente (%)" type="number" value={form.frais_vente} onChange={(v) => set("frais_vente", v)} testid="client-frais" />
          <FormField label="Titulaire du compte" value={form.titulaire_compte} onChange={(v) => set("titulaire_compte", v)} testid="client-titulaire" />
          <Row>
            <FormField label="IBAN" value={form.iban} onChange={(v) => set("iban", v)} testid="client-iban" />
            <FormField label="BIC" value={form.bic} onChange={(v) => set("bic", v)} testid="client-bic" />
          </Row>
        </Group>

        <Group title="Observations">
          <textarea
            data-testid="client-observations"
            value={form.observations}
            onChange={(e) => set("observations", e.target.value)}
            rows={3}
            className="w-full px-4 py-3 rounded-xl bg-[var(--vidoc-bg-secondary)] border border-transparent focus:border-[var(--vidoc-primary)] focus:bg-white outline-none resize-none"
          />
        </Group>

        <div className="sticky bottom-20 md:bottom-4 pt-10 mt-6">
          <button
            data-testid="client-save-btn"
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[var(--vidoc-primary)] text-white font-semibold touch-target disabled:opacity-50 hover:bg-[var(--vidoc-primary-dark)] shadow-lg shadow-blue-500/20"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? "Enregistrer les modifications" : "Créer le client"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <section>
      <h2 className="text-sm font-semibold mb-2">{title}</h2>
      <div className="bg-white rounded-xl border border-[var(--vidoc-border)] p-4 space-y-3">
        {children}
      </div>
    </section>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>;
}

function FormField({ label, required, value, onChange, type = "text", hint, testid }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-[var(--vidoc-text-secondary)] mb-1">
        {label}
        {required && <span className="text-[var(--vidoc-danger)] ml-1">*</span>}
      </div>
      <input
        data-testid={testid}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-lg bg-[var(--vidoc-bg-secondary)] border border-transparent focus:border-[var(--vidoc-primary)] focus:bg-white outline-none touch-target"
      />
      {hint && <div className="text-xs text-[var(--vidoc-text-tertiary)] mt-1">{hint}</div>}
    </label>
  );
}
